import React, { useState, useEffect, useRef } from 'react';
import s from './OpsMapDashboard.module.scss';
import OpsMapView from './components/OpsMapView';
import OpsMapStats from './components/OpsMapStats';
import MapLegendPanel from './components/MapLegendPanel';
import MapCoveragePanel from './components/MapCoveragePanel';
import TimeWindowFilter from './components/TimeWindowFilter';
import { useDateFilter } from '../../contexts/DateFilterContext';
import { useOpsMapState } from '../../contexts/OpsMapStateContext';
import { useMapData } from '../../contexts/MapDataContext';
import config from '../../config';
import { useGoogleMaps } from '../../context/GoogleMapsProvider';
import PlacesAutocompleteInput from '../../components/PlacesAutocompleteInput';
import VehicleDetails from '../dispatch/VehicleDetails';

const DEFAULT_VEHICLE_CLASS_OPTIONS = Array.from({ length: 15 }, (_, i) => i + 1);
const LEAD_HEATMAP_STATUSES = ['new', 'contacted', 'follow_up', 'converted', 'dropped'];

const OpsMapDashboard = () => {
    const { isLoaded } = useGoogleMaps();
    const { dateRange, setToday } = useDateFilter();

    // ---- Address search state ----
    const [searchQuery, setSearchQuery] = useState('');
    const [detailsVehicleId, setDetailsVehicleId] = useState(null);

    // Set default date filter to today if not set
    useEffect(() => {
        if (!dateRange.startDate && !dateRange.endDate) {
            setToday();
        }
    }, [dateRange.startDate, dateRange.endDate, setToday]);
    const {
        drivers,
        registeredDrivers,
        totalVehicles,
        bookings,
        districtMapping,
        selectedTimeWindow,
        setSelectedTimeWindow,
        isLive,
        setIsLive,
        loading,
        lastUpdated,
        fetchError,
        leadHeatmap,
        leadHeatmapLoading,
        fetchLeadHeatmap,
    } = useMapData();
    const {
        selectedDriver, setSelectedDriver,
        selectedBooking, setSelectedBooking,
        mapViewport, setMapViewport
    } = useOpsMapState();
    const [driverStatusFilter, setDriverStatusFilter] = useState('all');
    const [clusterDrivers, setClusterDrivers] = useState(null);
    const [focusSingleDriver, setFocusSingleDriver] = useState(false);
    const mapInstanceRef = useRef(null);
    const [showServiceZone, setShowServiceZone] = useState(true);
    const [showHotzones, setShowHotzones] = useState(true);
    const [showRealtimeHotzones, setShowRealtimeHotzones] = useState(true);
    const [showHistoricalHotzones, setShowHistoricalHotzones] = useState(true);
    const [showLeadHeatmap, setShowLeadHeatmap] = useState(false);
    const [leadHeatmapStatuses, setLeadHeatmapStatuses] = useState(LEAD_HEATMAP_STATUSES);
    const [leadTonnageMode, setLeadTonnageMode] = useState('all');
    const [leadTonnageMin, setLeadTonnageMin] = useState('');
    const [leadTonnageMax, setLeadTonnageMax] = useState('');
    const [selectedLeadDestination, setSelectedLeadDestination] = useState(null);
    const [showDrivers, setShowDrivers] = useState(true);
    const [showRegisteredVehicles, setShowRegisteredVehicles] = useState(false);
    const [showTransportersOnly, setShowTransportersOnly] = useState(false);
    const [vehicleClassFilter, setVehicleClassFilter] = useState([]);
    const [showBookings, setShowBookings] = useState(true);
    const [showCriticalOnly, setShowCriticalOnly] = useState(false);
    const [showCompletedBookings, setShowCompletedBookings] = useState(false);
    const [showServiceabilityEditor, setShowServiceabilityEditor] = useState(false);
    const [serviceabilityData, setServiceabilityData] = useState([]);
    const [serviceabilityLoading, setServiceabilityLoading] = useState(false);
    const [serviceabilitySaving, setServiceabilitySaving] = useState(false);
    const [expandedCityId, setExpandedCityId] = useState(null);
    const [serviceZoneRefreshKey, setServiceZoneRefreshKey] = useState(0);
    const [serviceabilityStateFilter, setServiceabilityStateFilter] = useState('all');
    const [serviceabilityDistrictFilter, setServiceabilityDistrictFilter] = useState('all');
    const [serviceabilityCitySearch, setServiceabilityCitySearch] = useState('');
    const [serviceabilityPincodeSearch, setServiceabilityPincodeSearch] = useState('');
    const [serviceabilityPage, setServiceabilityPage] = useState(0);
    const [serviceabilityHasMore, setServiceabilityHasMore] = useState(false);
    const [serviceabilityPageInput, setServiceabilityPageInput] = useState(1);
    const SERVICEABILITY_PER_PAGE = 20;
    const [showAddressManager, setShowAddressManager] = useState(false);
    const [addresses, setAddresses] = useState([]);
    const [addressesLoading, setAddressesLoading] = useState(false);
    const [addressesSaving, setAddressesSaving] = useState(false);
    const [editingAddress, setEditingAddress] = useState(null);
    const [addressError, setAddressError] = useState(null);
    const [addressSearchQuery, setAddressSearchQuery] = useState('');
    const [addressPage, setAddressPage] = useState(0);
    const [addressHasMore, setAddressHasMore] = useState(false);
    const [addressPageInput, setAddressPageInput] = useState(1);
    const ADDRESSES_PER_PAGE = 15;

    // Onboarding Coverage States
    const [citiesList, setCitiesList] = useState([]);
    const [selectedCities, setSelectedCities] = useState(() => {
        try {
            const saved = localStorage.getItem('ops_coverage_cities');
            return saved ? JSON.parse(saved) : [];
        } catch (e) {
            return [];
        }
    });
    const [pincodeAnalysis, setPincodeAnalysis] = useState(null);
    const [analysisLoading, setAnalysisLoading] = useState(false);
    const isInitialLoadRef = useRef(selectedCities.length > 0);

    const dispatchBaseUrl = config.dispatchApiBase || 'http://localhost:8001/api';

    // Fetch cities and pincodes on mount
    useEffect(() => {
        const fetchCitiesAndPincodes = async () => {
            try {
                const response = await fetch(`${dispatchBaseUrl}/zones/cities-with-pincodes?t=${Date.now()}`);
                const data = await response.json();
                if (data && data.success && Array.isArray(data.cities)) {
                    setCitiesList(data.cities);
                }
            } catch (error) {
                console.error('Error fetching cities with pincodes:', error);
            }
        };
        fetchCitiesAndPincodes();
    }, [dispatchBaseUrl]);

    // Fetch pincode analysis when selected cities change
    useEffect(() => {
        if (selectedCities.length === 0) {
            setPincodeAnalysis(null);
            return;
        }

        const fetchPincodeAnalysis = async () => {
            setAnalysisLoading(true);
            try {
                const url = `${dispatchBaseUrl}/zones/pincode-onboarding-analysis?city=${encodeURIComponent(selectedCities.join(','))}`;
                const response = await fetch(url);
                const data = await response.json();
                if (data && data.success) {
                    setPincodeAnalysis(data);
                } else {
                    setPincodeAnalysis(null);
                }
            } catch (error) {
                console.error('Error fetching pincode onboarding analysis:', error);
                setPincodeAnalysis(null);
            } finally {
                setAnalysisLoading(false);
            }
        };

        fetchPincodeAnalysis();
    }, [selectedCities, dispatchBaseUrl]);

    useEffect(() => {
        if (!showLeadHeatmap) return;
        fetchLeadHeatmap(leadHeatmapStatuses);
    }, [showLeadHeatmap, leadHeatmapStatuses, fetchLeadHeatmap]);

    // Drop a stale destination popup whenever the layer or its filters change.
    useEffect(() => {
        setSelectedLeadDestination(null);
    }, [showLeadHeatmap, leadHeatmapStatuses, leadTonnageMode, leadTonnageMin, leadTonnageMax]);

    const filteredLeadDestinations = React.useMemo(() => {
        const dests = leadHeatmap?.destinations || [];
        const minVal = leadTonnageMin === '' ? null : Number(leadTonnageMin);
        const maxVal = leadTonnageMax === '' ? null : Number(leadTonnageMax);
        return dests.filter((d) => {
            if (leadTonnageMode === 'known' && !d.known_tonnage_count) return false;
            const tonnes = d.total_tonnes;
            if (minVal != null && Number.isFinite(minVal)) {
                if (tonnes == null || tonnes < minVal) return false;
            }
            if (maxVal != null && Number.isFinite(maxVal)) {
                if (tonnes == null || tonnes > maxVal) return false;
            }
            return true;
        });
    }, [leadHeatmap, leadTonnageMode, leadTonnageMin, leadTonnageMax]);

    const handleCityToggle = (city) => {
        isInitialLoadRef.current = false;
        let updated;
        if (selectedCities.includes(city)) {
            updated = selectedCities.filter(c => c !== city);
        } else {
            updated = [...selectedCities, city];
        }
        setSelectedCities(updated);
        localStorage.setItem('ops_coverage_cities', JSON.stringify(updated));
        if (updated.length === 0) {
            setPincodeAnalysis(null);
        }
    };

    const handleResetCoverage = () => {
        isInitialLoadRef.current = false;
        setSelectedCities([]);
        localStorage.removeItem('ops_coverage_cities');
        setPincodeAnalysis(null);
    };

    const loadServiceabilitySettings = React.useCallback(async ({
        page = 0,
        state = 'all',
        district = 'all',
        citySearch = '',
        pincodeSearch = '',
    } = {}) => {
        setServiceabilityLoading(true);
        try {
            const params = new URLSearchParams({
                page,
                limit: SERVICEABILITY_PER_PAGE,
            });
            if (state !== 'all') params.set('state', state);
            if (district !== 'all') params.set('district', district);
            if (citySearch) params.set('city_search', citySearch);
            if (pincodeSearch) params.set('pincode_search', pincodeSearch);

            const response = await fetch(`${dispatchBaseUrl}/zones/serviceability-settings?${params}`);
            const data = await response.json();
            if (data && data.success && Array.isArray(data.cities)) {
                setServiceabilityData(data.cities);
                setServiceabilityHasMore(data.has_more || false);
            } else {
                setServiceabilityData([]);
                setServiceabilityHasMore(false);
            }
        } catch (error) {
            console.error('Error loading serviceability settings:', error);
            setServiceabilityData([]);
            setServiceabilityHasMore(false);
        } finally {
            setServiceabilityLoading(false);
        }
    }, [dispatchBaseUrl]);

    const [serviceabilityStateOptions, setServiceabilityStateOptions] = useState([]);
    const [serviceabilityDistrictOptions, setServiceabilityDistrictOptions] = useState([]);

    const loadServiceabilityFilterOptions = React.useCallback(async () => {
        try {
            const response = await fetch(`${dispatchBaseUrl}/zones/serviceability-filters`);
            const data = await response.json();
            if (data && data.success) {
                setServiceabilityStateOptions(data.states || []);
                setServiceabilityDistrictOptions(data.districts || []);
            }
        } catch (error) {
            console.error('Error loading serviceability filter options:', error);
        }
    }, [dispatchBaseUrl]);

    const openServiceabilityEditor = React.useCallback(async () => {
        setShowServiceabilityEditor(true);
        setServiceabilityStateFilter('all');
        setServiceabilityDistrictFilter('all');
        setServiceabilityCitySearch('');
        setServiceabilityPincodeSearch('');
        setServiceabilityPage(0);
        loadServiceabilityFilterOptions();
        await loadServiceabilitySettings({ page: 0 });
    }, [loadServiceabilitySettings, loadServiceabilityFilterOptions]);

    const loadAddresses = React.useCallback(async (pageNum = 0, searchQuery = '') => {
        setAddressesLoading(true);
        setAddressError(null);
        try {
            const params = new URLSearchParams({
                page: pageNum,
                limit: ADDRESSES_PER_PAGE,
                ...(searchQuery && { search: searchQuery })
            });
            const response = await fetch(`${dispatchBaseUrl}/addresses/list?${params}`);
            const data = await response.json();
            if (data && data.success && Array.isArray(data.addresses)) {
                setAddresses(data.addresses);
                setAddressHasMore(data.has_more || false);
            } else {
                setAddresses([]);
                setAddressHasMore(false);
            }
        } catch (error) {
            console.error('Error loading addresses:', error);
            setAddressError('Failed to load addresses');
            setAddresses([]);
            setAddressHasMore(false);
        } finally {
            setAddressesLoading(false);
        }
    }, [dispatchBaseUrl]);

    const openAddressManager = React.useCallback(async () => {
        setShowAddressManager(true);
        setEditingAddress(null);
        setAddressError(null);
        setAddressSearchQuery('');
        setAddressPage(0);
        await loadAddresses(0, '');
    }, [loadAddresses]);

    const handleDeleteAddress = React.useCallback(async (id) => {
        if (!window.confirm('Are you sure you want to delete this address?')) {
            return;
        }

        setAddressesSaving(true);
        setAddressError(null);
        try {
            const response = await fetch(`${dispatchBaseUrl}/addresses/delete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id }),
            });
            const data = await response.json();

            if (!response.ok) {
                setAddressError(data.detail || 'Failed to delete address');
                return;
            }

            setAddressError(null);
            await loadAddresses(addressPage, addressSearchQuery);
        } catch (error) {
            console.error('Error deleting address:', error);
            setAddressError('Error deleting address');
        } finally {
            setAddressesSaving(false);
        }
    }, [dispatchBaseUrl, loadAddresses, addressPage, addressSearchQuery]);

    const handleSaveAddress = React.useCallback(async (updatedData) => {
        setAddressesSaving(true);
        setAddressError(null);
        try {
            const response = await fetch(`${dispatchBaseUrl}/addresses/update`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedData),
            });
            const data = await response.json();

            if (!response.ok) {
                setAddressError(data.detail || 'Failed to save address');
                return;
            }

            setAddressError(null);
            setEditingAddress(null);
            await loadAddresses(addressPage, addressSearchQuery);
        } catch (error) {
            console.error('Error saving address:', error);
            setAddressError('Error saving address');
        } finally {
            setAddressesSaving(false);
        }
    }, [dispatchBaseUrl, loadAddresses, addressPage, addressSearchQuery]);

    const fetchCurrentServiceabilityPage = React.useCallback((pageOverride) => {
        const p = pageOverride !== undefined ? pageOverride : serviceabilityPage;
        loadServiceabilitySettings({
            page: p,
            state: serviceabilityStateFilter,
            district: serviceabilityDistrictFilter,
            citySearch: serviceabilityCitySearch,
            pincodeSearch: serviceabilityPincodeSearch,
        });
    }, [loadServiceabilitySettings, serviceabilityPage, serviceabilityStateFilter, serviceabilityDistrictFilter, serviceabilityCitySearch, serviceabilityPincodeSearch]);

    const handleServiceabilityFilterChange = React.useCallback((updates) => {
        const newState = {
            state: updates.stateFilter !== undefined ? updates.stateFilter : serviceabilityStateFilter,
            district: updates.districtFilter !== undefined ? updates.districtFilter : (updates.stateFilter !== undefined ? 'all' : serviceabilityDistrictFilter),
            citySearch: updates.citySearch !== undefined ? updates.citySearch : serviceabilityCitySearch,
            pincodeSearch: updates.pincodeSearch !== undefined ? updates.pincodeSearch : serviceabilityPincodeSearch,
        };
        setServiceabilityStateFilter(newState.state);
        setServiceabilityDistrictFilter(newState.district);
        setServiceabilityCitySearch(newState.citySearch);
        setServiceabilityPincodeSearch(newState.pincodeSearch);
        setServiceabilityPage(0);
        loadServiceabilitySettings({
            page: 0,
            state: newState.state,
            district: newState.district,
            citySearch: newState.citySearch,
            pincodeSearch: newState.pincodeSearch,
        });
    }, [loadServiceabilitySettings, serviceabilityStateFilter, serviceabilityDistrictFilter, serviceabilityCitySearch, serviceabilityPincodeSearch]);

    const toggleCityServiceability = React.useCallback(async (cityId, isServiceable) => {
        setServiceabilitySaving(true);
        try {
            const response = await fetch(`${dispatchBaseUrl}/zones/serviceability/city-toggle`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ city_id: cityId, is_serviceable: isServiceable }),
            });

            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.detail || 'Failed to update city serviceability');
            }

            await loadServiceabilitySettings({
                page: serviceabilityPage,
                state: serviceabilityStateFilter,
                district: serviceabilityDistrictFilter,
                citySearch: serviceabilityCitySearch,
                pincodeSearch: serviceabilityPincodeSearch,
            });
            setServiceZoneRefreshKey((v) => v + 1);
        } catch (error) {
            console.error('Error updating city serviceability:', error);
            alert('Failed to update city serviceability. Please try again.');
        } finally {
            setServiceabilitySaving(false);
        }
    }, [dispatchBaseUrl, loadServiceabilitySettings, serviceabilityPage, serviceabilityStateFilter, serviceabilityDistrictFilter, serviceabilityCitySearch, serviceabilityPincodeSearch]);

    const togglePincodeServiceability = React.useCallback(async (cityId, pincode, isServiceable) => {
        setServiceabilitySaving(true);
        try {
            const response = await fetch(`${dispatchBaseUrl}/zones/serviceability/pincode-toggle`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ city_id: cityId, pincode, is_serviceable: isServiceable }),
            });

            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.detail || 'Failed to update pincode serviceability');
            }

            await loadServiceabilitySettings({
                page: serviceabilityPage,
                state: serviceabilityStateFilter,
                district: serviceabilityDistrictFilter,
                citySearch: serviceabilityCitySearch,
                pincodeSearch: serviceabilityPincodeSearch,
            });
            setServiceZoneRefreshKey((v) => v + 1);
        } catch (error) {
            console.error('Error updating pincode serviceability:', error);
            alert('Failed to update pincode serviceability. Please try again.');
        } finally {
            setServiceabilitySaving(false);
        }
    }, [dispatchBaseUrl, loadServiceabilitySettings, serviceabilityPage, serviceabilityStateFilter, serviceabilityDistrictFilter, serviceabilityCitySearch, serviceabilityPincodeSearch]);

    const setFilteredCitiesServiceability = React.useCallback(async (isServiceable) => {
        setServiceabilitySaving(true);
        try {
            const payload = { is_serviceable: isServiceable };
            if (serviceabilityStateFilter !== 'all') payload.state = serviceabilityStateFilter;
            if (serviceabilityDistrictFilter !== 'all') payload.district = serviceabilityDistrictFilter;
            if (serviceabilityCitySearch) payload.city_search = serviceabilityCitySearch;
            if (serviceabilityPincodeSearch) payload.pincode_search = serviceabilityPincodeSearch;

            const response = await fetch(`${dispatchBaseUrl}/zones/serviceability/bulk-city-toggle-by-filter`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.detail || 'Failed to bulk update city serviceability');
            }

            await loadServiceabilitySettings({
                page: serviceabilityPage,
                state: serviceabilityStateFilter,
                district: serviceabilityDistrictFilter,
                citySearch: serviceabilityCitySearch,
                pincodeSearch: serviceabilityPincodeSearch,
            });
            setServiceZoneRefreshKey((v) => v + 1);
        } catch (error) {
            console.error('Error bulk updating city serviceability:', error);
            alert('Failed to bulk update serviceability. Please try again.');
        } finally {
            setServiceabilitySaving(false);
        }
    }, [dispatchBaseUrl, loadServiceabilitySettings, serviceabilityPage, serviceabilityStateFilter, serviceabilityDistrictFilter, serviceabilityCitySearch, serviceabilityPincodeSearch]);

    // Handle time window change
    const handleTimeWindowChange = (window) => {
        setSelectedTimeWindow(window);
    };

    // Memoize and merge drivers with their respective types
    const combinedDrivers = React.useMemo(() => {
        const live = drivers.map(d => ({ ...d, type: 'live' }));
        const reg = (registeredDrivers || []).map(d => ({ ...d, type: 'registered' }));
        return [...live, ...reg];
    }, [drivers, registeredDrivers]);

    // Gate: toggle ON = only registered, toggle OFF = only live
    const visibleDrivers = React.useMemo(() => {
        if (showRegisteredVehicles) {
            return combinedDrivers.filter(d => d.type === 'registered');
        }
        return combinedDrivers.filter(d => d.type !== 'registered');
    }, [combinedDrivers, showRegisteredVehicles]);

    const getVehicleClassNumber = (value) => {
        if (value == null) return null;
        if (typeof value === 'number' && Number.isFinite(value)) return value;
        const match = String(value).match(/(\d+)/);
        return match ? parseInt(match[1], 10) : null;
    };

    const vehicleClassOptions = React.useMemo(() => {
        const extras = new Set();
        visibleDrivers.forEach((driver) => {
            const classNum = getVehicleClassNumber(
                driver.vehicle_class ?? driver.vehicle_class_id ?? driver.vehicleClass ?? driver.class
            );
            if (classNum != null && (classNum < 1 || classNum > 15)) {
                extras.add(classNum);
            }
        });
        return DEFAULT_VEHICLE_CLASS_OPTIONS.concat(Array.from(extras).sort((a, b) => a - b));
    }, [visibleDrivers]);

    const vehicleClassFilteredDrivers = React.useMemo(() => {
        if (!vehicleClassFilter.length) return visibleDrivers;
        return visibleDrivers.filter((driver) => {
            const classNum = getVehicleClassNumber(
                driver.vehicle_class ?? driver.vehicle_class_id ?? driver.vehicleClass ?? driver.class
            );
            return classNum != null && vehicleClassFilter.includes(classNum);
        });
    }, [visibleDrivers, vehicleClassFilter]);

    // Filter drivers based on selected filter AND registered toggle
    const filteredMapDrivers = React.useMemo(() => {
        let list = vehicleClassFilteredDrivers;
        if (driverStatusFilter === 'registered') {
            list = list.filter(d => d.type === 'registered');
        } else if (driverStatusFilter === 'active') {
            list = list.filter(d => d.type === 'live' && d.driver_status === 'active');
        } else if (driverStatusFilter === 'available') {
            list = list.filter(d => d.type === 'live' && d.driver_status === 'available');
        } else if (driverStatusFilter !== 'all') {
            list = list.filter(d => d.driver_status === driverStatusFilter);
        }
        if (showTransportersOnly) {
            list = list.filter(d => !!d.is_transporter);
        } else {
            list = list.filter(d => !d.is_transporter);
        }
        return list;
    }, [vehicleClassFilteredDrivers, driverStatusFilter, showTransportersOnly]);

    // Override is_unassigned based on real-time WebSocket data
    const memoizedBookings = React.useMemo(() => bookings, [bookings]);

    // Apply Critical Only filter exclusively for the map
    const memoizedMapBookings = React.useMemo(() => {
        if (!showCriticalOnly) return memoizedBookings;

        return memoizedBookings.filter(booking => {
            let effectiveStatus = booking.state;
            if (booking.is_unassigned) {
                effectiveStatus = 'Unassigned';
            } else if (booking.escalation) {
                effectiveStatus = 'Delayed';
            } else if (booking.state === 'Pending') {
                const now = new Date();
                const pickupTime = new Date(booking.pickup_time);
                if (pickupTime < now) {
                    const isToday = pickupTime.toDateString() === now.toDateString();
                    effectiveStatus = isToday ? 'Overdue' : 'Unassigned';
                }
            }
            return effectiveStatus === 'Unassigned' || effectiveStatus === 'Delayed' || effectiveStatus === 'Overdue';
        });
    }, [memoizedBookings, showCriticalOnly]);

    // Sync addressPageInput to addressPage (0-indexed -> 1-indexed display)
    React.useEffect(() => {
        setAddressPageInput(addressPage + 1);
    }, [addressPage]);

    const handleAddressPageInputChange = (e) => {
        setAddressPageInput(e.target.value);
    };

    const handleAddressPageInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            const num = parseInt(addressPageInput, 10);
            if (Number.isFinite(num) && num >= 1) {
                const newPage = num - 1;
                setAddressPage(newPage);
                loadAddresses(newPage, addressSearchQuery);
            } else {
                setAddressPageInput(addressPage + 1);
            }
        }
    };

    const handleAddressPageInputBlur = () => {
        setAddressPageInput(addressPage + 1);
    };

    React.useEffect(() => {
        setServiceabilityPageInput(serviceabilityPage + 1);
    }, [serviceabilityPage]);

    const handleServiceabilityPageInputChange = (e) => {
        setServiceabilityPageInput(e.target.value);
    };

    const handleServiceabilityPageInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            const num = parseInt(serviceabilityPageInput, 10);
            if (Number.isFinite(num) && num >= 1) {
                const newPage = num - 1;
                setServiceabilityPage(newPage);
                loadServiceabilitySettings({
                    page: newPage,
                    state: serviceabilityStateFilter,
                    district: serviceabilityDistrictFilter,
                    citySearch: serviceabilityCitySearch,
                    pincodeSearch: serviceabilityPincodeSearch,
                });
            } else {
                setServiceabilityPageInput(serviceabilityPage + 1);
            }
        }
    };

    const handleServiceabilityPageInputBlur = () => {
        setServiceabilityPageInput(serviceabilityPage + 1);
    };

    const goToServiceabilityPage = (newPage) => {
        const clamped = Math.max(0, newPage);
        setServiceabilityPage(clamped);
        loadServiceabilitySettings({
            page: clamped,
            state: serviceabilityStateFilter,
            district: serviceabilityDistrictFilter,
            citySearch: serviceabilityCitySearch,
            pincodeSearch: serviceabilityPincodeSearch,
        });
    };

    return (
        <div className={s.root}>
            {/* Top Control Bar */}
            <div className={s.controlsBar}>
                <div className="d-flex align-items-center justify-content-between w-100">
                    <div className="d-flex align-items-center flex-nowrap">
                        <h4 className="mb-0 mr-4" style={{ whiteSpace: 'nowrap' }}>Ops Map</h4>
                        <TimeWindowFilter
                            selectedWindow={selectedTimeWindow}
                            onWindowChange={handleTimeWindowChange}
                            isLive={isLive}
                            setIsLive={setIsLive}
                        />

                        {/* Address Search Box — uses same REST API as VehicleDetails */}
                        <div className="ml-4" style={{ width: '300px' }}>
                            <PlacesAutocompleteInput
                                value={searchQuery}
                                onChange={setSearchQuery}
                                onPlaceSelect={({ address, lat, lng }) => {
                                    setSearchQuery(address);
                                    setMapViewport({
                                        center: { lat: parseFloat(lat), lng: parseFloat(lng) },
                                        zoom: 14,
                                    });
                                }}
                                placeholder="Search address or location..."
                                inputStyle={{
                                    height: '32px',
                                    fontSize: '13px',
                                    border: '1px solid #ced4da',
                                    borderRadius: '6px',
                                    padding: '0 10px',
                                    outline: 'none',
                                    color: '#334155',
                                }}
                            />
                        </div>
                    </div>
                    <span className="small text-muted" style={{ whiteSpace: 'nowrap' }}>
                        {fetchError && <i className="fa fa-exclamation-triangle text-warning mr-1"></i>}
                        {loading ? (
                            <span><i className="fa fa-refresh fa-spin mr-1"></i> Updating...</span>
                        ) : (
                            <span>⏱ Updated: {lastUpdated.toLocaleTimeString()}</span>
                        )}
                    </span>
                </div>
            </div>

            <div className={s.mainContent}>
                {/* Map Container */}
                <div className={s.mapContainer}>
                    <MapLegendPanel />
                    <MapCoveragePanel
                        citiesList={citiesList}
                        selectedCities={selectedCities}
                        onCityToggle={handleCityToggle}
                        onResetCoverage={handleResetCoverage}
                        onSelectedCitiesChange={(updated) => {
                            setSelectedCities(updated);
                            localStorage.setItem('ops_coverage_cities', JSON.stringify(updated));
                            if (updated.length === 0) {
                                setPincodeAnalysis(null);
                            }
                        }}
                        analysisLoading={analysisLoading}
                    />
                    <OpsMapView
                        drivers={showDrivers ? filteredMapDrivers : []}
                        bookings={showBookings ? memoizedMapBookings : []}
                        selectedDriver={selectedDriver}
                        selectedBooking={selectedBooking}
                        onSelectDriver={(driver) => {
                            setSelectedDriver(driver);
                            setFocusSingleDriver(false); // map click = show all co-located
                            if (!driver) return;
                            setClusterDrivers((prev) => {
                                if (!prev?.length) return prev;
                                const id = String(driver.driver_id || driver.id);
                                const inCluster = prev.some(
                                    (d) => String(d.driver_id || d.id) === id
                                );
                                return inCluster ? prev : null;
                            });
                        }}
                        onSelectBooking={(booking) => {
                            setSelectedBooking(booking);
                            if (booking) setClusterDrivers(null);
                        }}
                        onClusterDriversSelect={setClusterDrivers}
                        focusSingleDriver={focusSingleDriver}
                        onMapReady={(mapInstance) => { mapInstanceRef.current = mapInstance; }}
                        mapViewport={mapViewport}
                        onViewportChange={setMapViewport}
                        showServiceZone={showServiceZone}
                        showHotzones={showHotzones}
                        showRealtimeHotzones={showRealtimeHotzones}
                        showHistoricalHotzones={showHistoricalHotzones}
                        showLeadHeatmap={showLeadHeatmap}
                        leadDestinations={filteredLeadDestinations}
                        selectedLeadDestination={selectedLeadDestination}
                        onSelectLeadDestination={setSelectedLeadDestination}
                        showCriticalOnly={showCriticalOnly}
                        showCompletedBookings={showCompletedBookings}
                        districtMapping={districtMapping}
                        serviceZoneRefreshKey={serviceZoneRefreshKey}
                        pincodeAnalysis={pincodeAnalysis}
                        skipInitialPincodeFit={isInitialLoadRef.current}
                        onViewVehicleDetails={(driver) => {
                            if (driver?.vehicle_id) setDetailsVehicleId(driver.vehicle_id);
                        }}
                    />
                </div>

                {/* Sidebar Stats */}
                <div className={s.sidebar}>
                    <OpsMapStats
                        driverCount={totalVehicles}
                        bookingCount={memoizedBookings.length}
                        timeWindow={selectedTimeWindow.preset}
                        bookings={memoizedBookings}
                        driversList={showTransportersOnly ? vehicleClassFilteredDrivers.filter(d => d.is_transporter) : vehicleClassFilteredDrivers.filter(d => !d.is_transporter)}
                        clusterDrivers={clusterDrivers}
                        selectedDriver={selectedDriver}
                        onClearClusterDrivers={() => setClusterDrivers(null)}
                        onViewVehicleDetails={(driver) => {
                            if (driver?.vehicle_id) setDetailsVehicleId(driver.vehicle_id);
                        }}
                        onClusterDriverClick={(driver) => {
                            setFocusSingleDriver(true); // cluster legend click = show only this vehicle
                            setSelectedDriver(driver);
                            setSelectedBooking(null);
                            // Imperatively pan + zoom the map to the selected vehicle
                            if (mapInstanceRef.current && driver) {
                                const lat = parseFloat(driver.current_lat);
                                const lng = parseFloat(driver.current_lng);
                                if (!isNaN(lat) && !isNaN(lng)) {
                                    mapInstanceRef.current.panTo({ lat, lng });
                                    if (mapInstanceRef.current.getZoom() < 15) {
                                        mapInstanceRef.current.setZoom(15);
                                    }
                                }
                            }
                        }}
                        driverStatusFilter={driverStatusFilter}
                        setDriverStatusFilter={setDriverStatusFilter}
                        onDriverClick={(driver) => {
                            setSelectedDriver(driver);
                            setSelectedBooking(null);
                            setClusterDrivers(null);
                        }}
                        onBookingClick={(booking) => {
                            setSelectedBooking(booking);
                            setSelectedDriver(null);
                            setClusterDrivers(null);
                        }}
                        onModifyServiceableRegions={openServiceabilityEditor}
                        onModifyAddresses={openAddressManager}
                        pincodeAnalysis={pincodeAnalysis}
                        onClearPincodeAnalysis={handleResetCoverage}
                        // Layer props passed down to sidebar
                        showServiceZone={showServiceZone}
                        setShowServiceZone={setShowServiceZone}
                        showHotzones={showHotzones}
                        setShowHotzones={setShowHotzones}
                        showRealtimeHotzones={showRealtimeHotzones}
                        setShowRealtimeHotzones={setShowRealtimeHotzones}
                        showHistoricalHotzones={showHistoricalHotzones}
                        setShowHistoricalHotzones={setShowHistoricalHotzones}
                        showLeadHeatmap={showLeadHeatmap}
                        setShowLeadHeatmap={setShowLeadHeatmap}
                        leadHeatmapStatuses={leadHeatmapStatuses}
                        setLeadHeatmapStatuses={setLeadHeatmapStatuses}
                        leadTonnageMode={leadTonnageMode}
                        setLeadTonnageMode={setLeadTonnageMode}
                        leadTonnageMin={leadTonnageMin}
                        setLeadTonnageMin={setLeadTonnageMin}
                        leadTonnageMax={leadTonnageMax}
                        setLeadTonnageMax={setLeadTonnageMax}
                        leadHeatmap={leadHeatmap}
                        leadHeatmapLoading={leadHeatmapLoading}
                        filteredLeadDestinationCount={filteredLeadDestinations.length}
                        showDrivers={showDrivers}
                        setShowDrivers={setShowDrivers}
                        showRegisteredVehicles={showRegisteredVehicles}
                        setShowRegisteredVehicles={setShowRegisteredVehicles}
                        showTransportersOnly={showTransportersOnly}
                        setShowTransportersOnly={setShowTransportersOnly}
                        vehicleClassOptions={vehicleClassOptions}
                        vehicleClassFilter={vehicleClassFilter}
                        setVehicleClassFilter={setVehicleClassFilter}
                        showBookings={showBookings}
                        setShowBookings={setShowBookings}
                        showCriticalOnly={showCriticalOnly}
                        setShowCriticalOnly={setShowCriticalOnly}
                        showCompletedBookings={showCompletedBookings}
                        setShowCompletedBookings={setShowCompletedBookings}
                    />

                    {bookings.length === 0 && !loading && (
                        <div className="alert alert-info mt-3">
                            <small>No bookings found for the selected time window. Try selecting a longer duration.</small>
                        </div>
                    )}
                </div>
            </div>

            {showServiceabilityEditor && (
                <div className={s.serviceabilityOverlay}>
                    <div className={s.serviceabilityModalCard}>
                        <div className={s.serviceabilityHeader}>
                            <div>
                                <strong>🌐 Modify Serviceable Regions</strong>
                                <div className={s.subtitle}>Filter by state/district/city/pincode, then toggle quickly</div>
                            </div>
                            <button
                                type="button"
                                className="btn btn-sm"
                                onClick={() => setShowServiceabilityEditor(false)}
                            >
                                <i className="fa fa-times" />
                            </button>
                        </div>
                        <div className={s.serviceabilityBody} style={{ padding: '20px' }}>
                            {(serviceabilityLoading || serviceabilitySaving) && (
                                <div className="mb-2 text-muted small">
                                    <i className="fa fa-refresh fa-spin mr-1" /> Updating...
                                </div>
                            )}

                            <div className={s.serviceabilityFilterPanel}>
                                <div className="row">
                                    <div className="col-md-3 mb-2 mb-md-0">
                                        <label className="small text-muted mb-1" style={{ fontWeight: '500' }}>State</label>
                                        <select
                                            className="form-control form-control-sm"
                                            value={serviceabilityStateFilter}
                                            onChange={(e) => handleServiceabilityFilterChange({ stateFilter: e.target.value })}
                                            style={{ borderColor: '#bfdbfe' }}
                                        >
                                            <option value="all">All States</option>
                                            {serviceabilityStateOptions.map((state) => (
                                                <option key={state} value={state}>{state}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-3 mb-2 mb-md-0">
                                        <label className="small text-muted mb-1" style={{ fontWeight: '500' }}>District</label>
                                        <select
                                            className="form-control form-control-sm"
                                            value={serviceabilityDistrictFilter}
                                            onChange={(e) => handleServiceabilityFilterChange({ districtFilter: e.target.value })}
                                            style={{ borderColor: '#bfdbfe' }}
                                        >
                                            <option value="all">All Districts</option>
                                            {serviceabilityDistrictOptions.map((district) => (
                                                <option key={district} value={district}>{district}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-3 mb-2 mb-md-0">
                                        <label className="small text-muted mb-1" style={{ fontWeight: '500' }}>City Search</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder="Search city..."
                                            value={serviceabilityCitySearch}
                                            onChange={(e) => handleServiceabilityFilterChange({ citySearch: e.target.value })}
                                            style={{ borderColor: '#bfdbfe' }}
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="small text-muted mb-1" style={{ fontWeight: '500' }}>Pincode Search</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder="e.g. 5600"
                                            value={serviceabilityPincodeSearch}
                                            onChange={(e) => handleServiceabilityFilterChange({ pincodeSearch: e.target.value })}
                                            style={{ borderColor: '#bfdbfe' }}
                                        />
                                    </div>
                                </div>
                                <div className="d-flex flex-wrap justify-content-between align-items-center mt-3" style={{ gap: '8px' }}>
                                    <div className="small text-muted" style={{ fontWeight: '500' }}>
                                        ✓ Page {serviceabilityPage + 1} · {serviceabilityData.length} cities shown
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-sm"
                                        onClick={() => handleServiceabilityFilterChange({ stateFilter: 'all', districtFilter: 'all', citySearch: '', pincodeSearch: '' })}
                                        style={{ background: 'linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%)', border: '1px solid #bfdbfe', color: '#1e40af', fontWeight: '500' }}
                                    >
                                        <i className="fa fa-refresh mr-1" /> Reset filters
                                    </button>
                                </div>
                                <div className="mt-3 d-flex" style={{ gap: '8px' }}>
                                    <button
                                        type="button"
                                        className="btn btn-sm"
                                        disabled={serviceabilitySaving || (serviceabilityData.length === 0 && !serviceabilityHasMore && serviceabilityPage === 0)}
                                        onClick={() => setFilteredCitiesServiceability(true)}
                                        style={{ background: 'linear-gradient(135deg, #dcfce7 0%, #d1f2e8 100%)', border: '1px solid #86efac', color: '#16a34a', fontWeight: '500' }}
                                    >
                                        <i className="fa fa-check mr-1" /> Make all serviceable
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm"
                                        disabled={serviceabilitySaving || (serviceabilityData.length === 0 && !serviceabilityHasMore && serviceabilityPage === 0)}
                                        onClick={() => setFilteredCitiesServiceability(false)}
                                        style={{ background: 'linear-gradient(135deg, #fee2e2 0%, #fecdd3 100%)', border: '1px solid #fca5a5', color: '#991b1b', fontWeight: '500' }}
                                    >
                                        <i className="fa fa-ban mr-1" /> Make all unserviceable
                                    </button>
                                </div>
                            </div>

                            {!serviceabilityLoading && serviceabilityData.length === 0 && (
                                <div className="alert alert-info mb-0">No serviceability rows found.</div>
                            )}

                            {serviceabilityData.map((city) => {
                                const isExpanded = expandedCityId === city.city_id;
                                const filteredPincodes = (city.pincodes || []).filter((pin) => {
                                    const pincodeSearch = serviceabilityPincodeSearch.replace(/\D/g, '');
                                    if (!pincodeSearch) return true;
                                    return String(pin.pincode || '').includes(pincodeSearch);
                                });
                                const hasExpandablePincodes = filteredPincodes.length > 0;
                                return (
                                    <div key={city.city_id} className={`${s.serviceabilityCityCard} border rounded p-2 mb-2`}>
                                        <div className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: '10px' }}>
                                            <div>
                                                <div className="font-weight-bold" style={{ fontSize: '15px' }}>{city.city}</div>
                                                <div className="small text-muted">
                                                    {city.district}, {city.state} · {city.hex_count} hexes
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                                                <label className="mb-0 mr-2 small text-muted">Serviceable</label>
                                                <input
                                                    type="checkbox"
                                                    checked={!!city.is_serviceable}
                                                    disabled={serviceabilitySaving}
                                                    onChange={(e) => toggleCityServiceability(city.city_id, e.target.checked)}
                                                />
                                                {hasExpandablePincodes && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-link btn-sm"
                                                        onClick={() => setExpandedCityId(isExpanded ? null : city.city_id)}
                                                    >
                                                        {isExpanded ? 'Hide pincodes' : `Show pincodes (${filteredPincodes.length})`}
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {isExpanded && hasExpandablePincodes && (
                                            <div className={`${s.serviceabilityPincodeList} mt-2 pl-2 border-left`}>
                                                {filteredPincodes.map((pin) => (
                                                    <div key={`${city.city_id}-${pin.pincode}`} className="d-flex justify-content-between align-items-center py-1">
                                                        <div className="small">
                                                            {pin.pincode}
                                                            <span className="text-muted ml-2">({pin.hex_count} hexes)</span>
                                                        </div>
                                                        <input
                                                            type="checkbox"
                                                            checked={!!pin.is_serviceable}
                                                            disabled={serviceabilitySaving}
                                                            onChange={(e) => togglePincodeServiceability(city.city_id, pin.pincode, e.target.checked)}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            {(serviceabilityHasMore || serviceabilityPage > 0) && (
                                <div className={s.addressPaginationFooter}>
                                    <div className={s.pageInfo}>
                                        Page
                                        <input
                                            type="number"
                                            min={1}
                                            value={serviceabilityPageInput}
                                            onChange={handleServiceabilityPageInputChange}
                                            onKeyDown={handleServiceabilityPageInputKeyDown}
                                            onBlur={handleServiceabilityPageInputBlur}
                                            className={s.pageInput}
                                        />
                                    </div>
                                    <div className={s.paginationButtons}>
                                        <button
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => goToServiceabilityPage(serviceabilityPage - 1)}
                                            disabled={serviceabilityPage === 0}
                                        >
                                            <i className="fa fa-chevron-left mr-1" /> Previous
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => goToServiceabilityPage(serviceabilityPage + 1)}
                                            disabled={!serviceabilityHasMore}
                                        >
                                            Next <i className="fa fa-chevron-right ml-1" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showAddressManager && (
                <div className={s.serviceabilityOverlay}>
                    <div className={s.serviceabilityModalCard} style={{ maxWidth: '1100px' }}>
                        <div className={s.addressManagerHeader} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                                <strong>📍 Manage Addresses</strong>
                                <div className={s.subtitle}>View, edit, or delete customer addresses</div>
                            </div>
                            <button
                                type="button"
                                className="btn btn-sm"
                                onClick={() => setShowAddressManager(false)}
                                style={{ background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', color: 'white', flexShrink: 0, marginLeft: '12px' }}
                            >
                                <i className="fa fa-times" />
                            </button>
                        </div>
                        <div className={s.addressManagerBody} style={{ padding: '20px' }}>
                            {addressError && (
                                <div className="alert alert-warning alert-dismissible fade show" role="alert" style={{ marginBottom: '12px' }}>
                                    {addressError}
                                    <button type="button" className="close" onClick={() => setAddressError(null)}>
                                        <span>&times;</span>
                                    </button>
                                </div>
                            )}

                            {(addressesLoading || addressesSaving) && (
                                <div className="mb-3 text-muted small">
                                    <i className="fa fa-refresh fa-spin mr-1" /> {addressesLoading ? 'Loading addresses...' : 'Saving...'}
                                </div>
                            )}

                            {editingAddress ? (
                                <div className={s.addressEditPanel}>
                                    <h6>
                                        <i className="fa fa-pencil mr-2" /> Edit Address #{editingAddress.id}
                                    </h6>
                                    <div className="row">
                                        <div className="col-md-6 mb-2">
                                            <label className="small text-muted" style={{ fontWeight: '500' }}>Pin Code</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                value={editingAddress.pin_code || ''}
                                                onChange={(e) => setEditingAddress({ ...editingAddress, pin_code: e.target.value })}
                                                placeholder="e.g., 560001"
                                            />
                                        </div>
                                        <div className="col-md-6 mb-2">
                                            <label className="small text-muted" style={{ fontWeight: '500' }}>Locality</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                value={editingAddress.locality || ''}
                                                onChange={(e) => setEditingAddress({ ...editingAddress, locality: e.target.value })}
                                                placeholder="e.g., Indiranagar"
                                            />
                                        </div>
                                        <div className="col-md-6 mb-2">
                                            <label className="small text-muted" style={{ fontWeight: '500' }}>City</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                value={editingAddress.city || ''}
                                                onChange={(e) => setEditingAddress({ ...editingAddress, city: e.target.value })}
                                                placeholder="e.g., Bangalore"
                                            />
                                        </div>
                                        <div className="col-md-6 mb-2">
                                            <label className="small text-muted" style={{ fontWeight: '500' }}>State</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                value={editingAddress.state || ''}
                                                onChange={(e) => setEditingAddress({ ...editingAddress, state: e.target.value })}
                                                placeholder="e.g., Karnataka"
                                            />
                                        </div>
                                        <div className="col-md-6 mb-2">
                                            <label className="small text-muted" style={{ fontWeight: '500' }}>Latitude</label>
                                            <input
                                                type="number"
                                                step="0.0001"
                                                className="form-control form-control-sm"
                                                value={editingAddress.latitude || ''}
                                                onChange={(e) => setEditingAddress({ ...editingAddress, latitude: e.target.value ? parseFloat(e.target.value) : null })}
                                                placeholder="e.g., 12.9716"
                                            />
                                        </div>
                                        <div className="col-md-6 mb-2">
                                            <label className="small text-muted" style={{ fontWeight: '500' }}>Longitude</label>
                                            <input
                                                type="number"
                                                step="0.0001"
                                                className="form-control form-control-sm"
                                                value={editingAddress.longitude || ''}
                                                onChange={(e) => setEditingAddress({ ...editingAddress, longitude: e.target.value ? parseFloat(e.target.value) : null })}
                                                placeholder="e.g., 77.5946"
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-3">
                                        <button
                                            className="btn btn-sm btn-success"
                                            onClick={() => handleSaveAddress(editingAddress)}
                                            disabled={addressesSaving}
                                        >
                                            <i className="fa fa-check mr-1" /> Save Changes
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-secondary ml-2"
                                            onClick={() => setEditingAddress(null)}
                                            disabled={addressesSaving}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            ) : null}

                            {/* Search Bar */}
                            <div className={s.addressSearchBox}>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="🔍 Search by ID, Pin Code, Locality, City, or State..."
                                    value={addressSearchQuery}
                                    onChange={(e) => {
                                        setAddressSearchQuery(e.target.value);
                                        setAddressPage(0);
                                        loadAddresses(0, e.target.value);
                                    }}
                                />
                            </div>

                            {/* Table Container */}
                            <div className={s.addressTableContainer}>
                                {addresses.length === 0 ? (
                                    <div className={s.emptyState}>
                                        <i className="fa fa-inbox" />
                                        <div>{addressSearchQuery ? 'No addresses match your search' : 'No addresses found'}</div>
                                    </div>
                                ) : (
                                    <div className="table-responsive" style={{ marginBottom: '0' }}>
                                        <table className="table table-sm table-hover mb-0">
                                            <thead className="bg-light" style={{ position: 'sticky', top: '0', zIndex: '1' }}>
                                                <tr>
                                                    <th style={{ width: '50px', fontSize: '12px' }}>ID</th>
                                                    <th style={{ fontSize: '12px' }}>Pin Code</th>
                                                    <th style={{ fontSize: '12px' }}>Locality</th>
                                                    <th style={{ fontSize: '12px' }}>City</th>
                                                    <th style={{ fontSize: '12px' }}>State</th>
                                                    <th style={{ fontSize: '12px' }}>Coordinates</th>
                                                    <th style={{ width: '170px', fontSize: '12px' }}>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {addresses.map((addr) => (
                                                    <tr key={addr.id} style={{ backgroundColor: editingAddress?.id === addr.id ? '#eff6ff' : 'transparent', borderBottom: '1px solid #f3f4f6' }}>
                                                        <td><small style={{ fontWeight: '500' }}>{addr.id}</small></td>
                                                        <td><small>{addr.pin_code || '-'}</small></td>
                                                        <td><small>{addr.locality || '-'}</small></td>
                                                        <td><small>{addr.city || '-'}</small></td>
                                                        <td><small>{addr.state || '-'}</small></td>
                                                        <td><small style={{ color: '#6b7280' }}>{addr.latitude ? `${addr.latitude.toFixed(4)}, ${addr.longitude.toFixed(4)}` : '-'}</small></td>
                                                        <td style={{ whiteSpace: 'nowrap' }}>
                                                            <div className={s.addressActionButtons}>
                                                                {addr.latitude && addr.longitude && (
                                                                    <button
                                                                        className={s.mapButton}
                                                                        onClick={() => {
                                                                            const mapsUrl = `https://maps.google.com/?q=${addr.latitude},${addr.longitude}`;
                                                                            window.open(mapsUrl, '_blank');
                                                                        }}
                                                                        title="Open in Google Maps"
                                                                    >
                                                                        <i className="fa fa-map-marker" />
                                                                    </button>
                                                                )}
                                                                <button
                                                                    className={s.editButton}
                                                                    onClick={() => setEditingAddress({ ...addr })}
                                                                    disabled={addressesSaving || editingAddress?.id === addr.id}
                                                                    title="Edit address"
                                                                >
                                                                    <i className="fa fa-edit" />
                                                                </button>
                                                                <button
                                                                    className={s.deleteButton}
                                                                    onClick={() => handleDeleteAddress(addr.id)}
                                                                    disabled={addressesSaving || editingAddress?.id === addr.id}
                                                                    title="Delete address"
                                                                >
                                                                    <i className="fa fa-trash" />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>

                            {/* Pagination Controls */}
                            {(addressHasMore || addressPage > 0) && (
                                <div className={s.addressPaginationFooter}>
                                    <div className={s.pageInfo}>
                                        Page
                                        <input
                                            type="number"
                                            min={1}
                                            value={addressPageInput}
                                            onChange={handleAddressPageInputChange}
                                            onKeyDown={handleAddressPageInputKeyDown}
                                            onBlur={handleAddressPageInputBlur}
                                            className={s.pageInput}
                                        />
                                    </div>
                                    <div className={s.paginationButtons}>
                                        <button
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => {
                                                const newPage = Math.max(0, addressPage - 1);
                                                setAddressPage(newPage);
                                                loadAddresses(newPage, addressSearchQuery);
                                            }}
                                            disabled={addressPage === 0}
                                        >
                                            <i className="fa fa-chevron-left mr-1" /> Previous
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => {
                                                const newPage = addressPage + 1;
                                                setAddressPage(newPage);
                                                loadAddresses(newPage, addressSearchQuery);
                                            }}
                                            disabled={!addressHasMore}
                                        >
                                            Next <i className="fa fa-chevron-right ml-1" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {detailsVehicleId && (
                <VehicleDetails
                    popupVehicleId={detailsVehicleId}
                    onPopupClose={() => setDetailsVehicleId(null)}
                />
            )}
        </div>
    );
};

export default OpsMapDashboard;
